import { useState, useCallback } from "react";
import {
  ApprovalEntity,
  ApprovalsResultEntity,
  PaginationInfoEntity,
  GetApprovalsUseCase,
  GetApprovalsParams,
} from "../../domain";
import { ApprovalRepositoryImpl } from "../../data/repositories/ApprovalRepositoryImpl";

// Create repository instance (singleton)
const approvalRepository = new ApprovalRepositoryImpl();

// Create use case
const getApprovalsUseCase = new GetApprovalsUseCase(approvalRepository);

export interface UseApprovalsReturn {
  approvals: ApprovalEntity[];
  pagination: PaginationInfoEntity | null;
  loading: boolean;
  error: string | null;
  fetchApprovals: (params?: GetApprovalsParams) => Promise<void>;
  refresh: () => Promise<void>;
  clearError: () => void;
}

export const useApprovals = (
  initialParams?: GetApprovalsParams
): UseApprovalsReturn => {
  const [approvals, setApprovals] = useState<ApprovalEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfoEntity | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentParams, setCurrentParams] = useState<
    GetApprovalsParams | undefined
  >(initialParams);

  /**
   * Fetch approvals with filters
   */
  const fetchApprovals = useCallback(
    async (params?: GetApprovalsParams): Promise<void> => {
      setLoading(true);
      setError(null);

      const paramsToUse = params || currentParams || {};

      // Clear approvals immediately if entity type or status changed to show loader
      const entityTypeChanged =
        paramsToUse.entityType !== currentParams?.entityType;
      const statusChanged = paramsToUse.status !== currentParams?.status;

      if (entityTypeChanged || statusChanged) {
        setApprovals([]);
        setPagination(null);
      }

      try {
        const result = await getApprovalsUseCase.execute(paramsToUse);
        setApprovals(result.approvals);
        setPagination(result.pagination);
        setCurrentParams(paramsToUse);
      } catch (err: any) {
        const errorMessage = err.message || "Failed to load approvals";
        setError(errorMessage);
        console.error("Error fetching approvals:", err);
      } finally {
        setLoading(false);
      }
    },
    [currentParams]
  );

  /**
   * Refresh approvals with current params
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchApprovals(currentParams);
  }, [fetchApprovals, currentParams]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    approvals,
    pagination,
    loading,
    error,
    fetchApprovals,
    refresh,
    clearError,
  };
};
